import classNames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';

import { Button } from '@hivebrite/design-system/Button';
import { zIndices } from '@hivebrite/design-system/Theme';

import ContentPreview from 'hb-react/shared/components/ContentPreview';
import Loader from 'hb-react/shared/components/Loader';
import { withFeatureFlag } from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { numberWithCurrency } from 'hb-react/shared/utils/formatNumber';

import BraintreeError from './errors/BraintreeError';
import { paymentOptions } from '../utils/braintree';

let BraintreeWebDropIn = null;

async function lazyLoadBraintreeWebDropIn() {
  // Only load one time BraintreeWebDropIn
  if (BraintreeWebDropIn === null) {
    BraintreeWebDropIn = await import('braintree-web-drop-in');
  }

  return BraintreeWebDropIn;
}

class PaymentBraintree extends Component {
  state = {
    isLoading: true,
    buttonIsDisabled: false,
  };

  // LIFECIRCLE

  componentDidMount() {
    const { getClientTokenUrl, failCallback } = this.props;

    axios
      .get(getClientTokenUrl)
      .then((response) => {
        this.createBraintreeDropIn({
          clientTokenFromServer: get(response, ['data', 'authorization_token']),
          config: get(response, ['data', 'braintree_configuration']),
        });
      })
      .catch((error) => {
        if (failCallback) {
          failCallback(error);
        }
        errorReporting.captureException(
          new BraintreeError("Can't get client token from HB", error),
        );
      });
  }

  componentWillUnmount() {
    if (this.instance?.teardown) {
      this.instance.teardown();
    }
  }

  // LOGIC

  disableButton = () => {
    this.setState({ buttonIsDisabled: true });
  };

  enableButton = () => {
    this.setState({ buttonIsDisabled: false });
  };

  createBraintreeDropIn = async ({ clientTokenFromServer, config }) => {
    const {
      flags,
      options,
      amount,
      currency,
      paymentIsRecurring,
      failCallback,
      onNoPaymentMethodRequestable,
      onPaymentMethodRequestable,
      onPaymentOptionSelected,
      onInstance,
    } = this.props;

    const { default: BraintreeWebDropIn } = await lazyLoadBraintreeWebDropIn();

    BraintreeWebDropIn.create(
      {
        authorization: clientTokenFromServer,
        container: this.container,
        threeDSecure: flags.payments.braintreeSca,
        locale: I18n.locale,
        ...paymentOptions({
          amount,
          currency: currency?.code || currency,
          config,
          paymentIsRecurring,
        }),
        ...options,
      },
      (error, instance) => {
        if (error) {
          if (failCallback) {
            failCallback(error);
          }
          errorReporting.captureException(
            new BraintreeError("Can't create Braintree DropIn", error),
          );
        }
        // Braintree DropIn is up
        this.instance = instance;
        this.setState({ isLoading: false });

        if (onInstance) {
          onInstance(instance);
        }
        if (onNoPaymentMethodRequestable) {
          instance.on(
            'noPaymentMethodRequestable',
            onNoPaymentMethodRequestable,
          );
        }
        if (onPaymentMethodRequestable) {
          instance.on('paymentMethodRequestable', onPaymentMethodRequestable);
        }
        if (onPaymentOptionSelected) {
          instance.on('paymentOptionSelected', onPaymentOptionSelected);
        }
      },
    );
  };

  executePayment = (nonce) => {
    const { additionalData, executePaymentUrl, successCallback, failCallback } =
      this.props;

    const braintreePayload = {
      transaction_token: nonce,
      gateway: 'Braintree',
      payment_method: 'card',
    };

    const data =
      additionalData instanceof Function
        ? additionalData(braintreePayload)
        : {
            ...additionalData,
            ...braintreePayload,
          };

    axios
      .post(executePaymentUrl, data)
      .then((response) => {
        if (successCallback) {
          successCallback(response);
        }
      })
      .catch((error) => {
        this.enableButton();
        if (failCallback) {
          failCallback(error);
        }
      });
  };

  // eslint-disable-next-line class-methods-use-this
  throw3DSecureError = (payload) => {
    if (!payload.threeDSecureInfo) {
      errorReporting.captureException(
        new BraintreeError('No 3DSecure response'),
      );
    } else if (!payload.threeDSecureInfo.liabilityShifted) {
      errorReporting.captureException(
        new BraintreeError(
          `3DSecure authentication failed, status: ${payload.threeDSecureInfo.status}`,
        ),
      );
    }
  };

  // eslint-disable-next-line class-methods-use-this
  threeDSecureParams = (amount, additionalData) => {
    const options = {
      threeDSecure: {
        amount,
      },
    };

    if (additionalData.receipt) {
      const userData = additionalData.receipt;

      options.threeDSecure.email = userData.email;
      options.threeDSecure.billingAddress = {
        givenName: userData.firstname,
        surname: userData.lastname,
        streetAddress: userData.billing_address_attributes.address_1,
        extendedAddress: userData.billing_address_attributes.address_2,
        locality: userData.billing_address_attributes.city,
        postalCode: userData.billing_address_attributes.postal_code,
        countryCodeAlpha2: userData.billing_address_attributes.country,
      };
    }

    return options;
  };

  // HANDLERS

  handlePurchaseClick = () => {
    const {
      flags,
      amount,
      additionalData,
      executePaymentUrl,
      onGetNonce,
      failCallback,
    } = this.props;

    this.disableButton();

    const options = flags.payments.braintreeSca
      ? this.threeDSecureParams(amount, additionalData)
      : {};

    this.instance.requestPaymentMethod(options, (error, payload) => {
      if (error) {
        this.enableButton();
        if (error.message !== 'No payment method is available.') {
          if (failCallback) {
            failCallback(error);
          }
          errorReporting.captureException(
            new BraintreeError(
              "Can't get payload & nonce from Braintree",
              error,
            ),
          );
        }

        return;
      }

      if (payload.type === 'CreditCard') {
        const threeDSecureError = !payload?.threeDSecureInfo?.liabilityShifted;

        if (flags.payments.braintreeSca && threeDSecureError) {
          if (failCallback) {
            failCallback({
              response: {
                data: {
                  errors: I18n.t('web.payments.braintree.three_d_secure_error'),
                },
              },
            });
          }
          this.throw3DSecureError(payload);

          return;
        }
      }

      // Braintree sends back the nonce
      if (executePaymentUrl) {
        this.executePayment(payload.nonce);
      }
      if (onGetNonce) {
        onGetNonce(payload.nonce);
      }
    });
  };

  // RENDERERS

  renderBraintreeContainer = () => {
    const { submitButtonText, amount, currency } = this.props;
    const { isLoading, buttonIsDisabled } = this.state;
    const textWithAmount = `${submitButtonText} ${numberWithCurrency(
      amount,
      currency.code || currency,
      { currencyDisplay: 'symbol' },
    )}`;

    return (
      <div data-testid="braintree-container">
        {isLoading && <Loader small={false} />}
        <div
          className={classNames({
            'deprecated--visuallyhidden': isLoading,
          })}
          ref={(ref) => (this.container = ref)}
        />
        <div className="text--center">
          <Button
            onClick={this.handlePurchaseClick}
            dataTestId="braintree-purchase-button"
            isDisabled={buttonIsDisabled}
            isLoading={isLoading}
          >
            {textWithAmount}
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const { displayInModal, closeBraintreeModal } = this.props;

    return displayInModal ? (
      <ContentPreview
        portalProps={{
          defaultOpen: true,
          closeOnEsc: true,
          onClose: () => closeBraintreeModal && closeBraintreeModal(),
        }}
        modalPreviewViewStyle={{ zIndex: zIndices.modal }}
        screenReadersTitle={s__('Accessibility|Payment window')}
        screenReadersCloseButtonText={s__('Accessibility|Close payment window')}
      >
        <div className="min-height-35-vh soft-double palm--soft flex flex-center flex--justify-center">
          <div className="width--100-percent">
            {this.renderBraintreeContainer()}
          </div>
        </div>
      </ContentPreview>
    ) : (
      this.renderBraintreeContainer()
    );
  }
}

PaymentBraintree.propTypes = {
  getClientTokenUrl: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currency: PropTypes.object.isRequired,
  failCallback: PropTypes.func.isRequired,
  paymentIsRecurring: PropTypes.bool,
  executePaymentUrl: PropTypes.string,
  submitButtonText: PropTypes.string,
  onGetNonce: PropTypes.func,
  options: PropTypes.object,
  additionalData: PropTypes.object,
  successCallback: PropTypes.func,
  onNoPaymentMethodRequestable: PropTypes.func,
  onPaymentMethodRequestable: PropTypes.func,
  onPaymentOptionSelected: PropTypes.func,
  onInstance: PropTypes.func,
  closeBraintreeModal: PropTypes.func,
  displayInModal: PropTypes.bool,
};

PaymentBraintree.defaultProps = {
  options: {},
  additionalData: {},
  paymentIsRecurring: false,
  displayInModal: false,
  submitButtonText: __('Purchase'),
};

export default withFeatureFlag(PaymentBraintree);
